import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { validateEmail } from '../utils/validation'
import { resetPasswordWeb } from '../services/authService'
import { PASSWORD_RESET_EMAIL_KEY } from '../utils/passwordRecovery'

const ForgotPasswordModal = ({ onClose, onBackToLogin }) => {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleChange = (e) => {
    setEmail(e.target.value)
    if (error) {
      setError('')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const emailValidation = validateEmail(email)
    if (!emailValidation.isValid) {
      setError(emailValidation.message)
      return
    }

    setLoading(true)
    setError('')

    try {
      const trimmedEmail = email.trim().toLowerCase()
      const result = await resetPasswordWeb(trimmedEmail)

      if (result.success) {
        sessionStorage.setItem(PASSWORD_RESET_EMAIL_KEY, trimmedEmail)
        onClose()
        navigate('/verify-reset-code', { state: { email: trimmedEmail } })
      } else {
        setError(result.error || 'Failed to send reset email')
      }
    } catch (err) {
      console.error('Password reset error:', err)
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6 sm:p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-6">
          <div className="flex-1">
            <h2 className="text-2xl sm:text-3xl font-bold">
              Forgot Password?
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Enter your email and we&apos;ll send a 6-digit reset code.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 ml-4">
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start">
                <i className="fas fa-exclamation-circle text-red-600 mr-2 mt-0.5"></i>
                <div className="flex-1">
                  <p className="text-red-600 text-sm font-medium">{error}</p>
                  {error.includes('rate limit') && (
                    <p className="text-red-500 text-xs mt-2">
                      <i className="fas fa-clock mr-1"></i>
                      Please wait a few minutes before requesting another reset email.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="mb-6">
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={email}
              onChange={handleChange}
              required
              placeholder="Enter your email"
              disabled={loading}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed ${
                error ? 'border-red-500' : 'border-gray-300'
              }`}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-secondary text-white py-3 rounded-lg hover:bg-gray-800 transition text-base sm:text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <i className="fas fa-spinner fa-spin mr-2"></i>
                Sending...
              </span>
            ) : (
              'Send reset code'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={onBackToLogin}
            className="text-primary hover:underline text-sm sm:text-base"
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Back to Sign In
          </button>
        </div>
      </div>
    </div>
  )
}

export default ForgotPasswordModal
